import React, { useState } from 'react';
import {
  CharacterMode,
  CombustionProfileId,
  EngineCategory,
  EngineProfileId,
  SpeedUnit,
  TransmissionMode,
  VehicleState,
} from '../types';
import { COMBUSTION_PROFILE_IDS, ENGINE_PROFILES, EV_PROFILE_IDS, isEVProfile } from '../config/engines';
import { EV_SOUND_PROFILES } from '../config/evProfiles';
import { AlertTriangle, Flame, ShieldCheck, Volume2, VolumeX, X, Zap } from 'lucide-react';

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleState: VehicleState;
  speedUnit: SpeedUnit;
  onSetSpeedUnit: (unit: SpeedUnit) => void;
  onSelectEngine: (profileId: EngineProfileId) => void;
  onSelectCharacter: (mode: CharacterMode) => void;
  onSelectTransmission: (mode: TransmissionMode) => void;
  onSetVolume: (vol: number) => void;
  onToggleMute: () => void;
}

export const SettingsDrawer: React.FC<SettingsDrawerProps> = ({
  isOpen,
  onClose,
  vehicleState,
  speedUnit,
  onSetSpeedUnit,
  onSelectEngine,
  onSelectCharacter,
  onSelectTransmission,
  onSetVolume,
  onToggleMute,
}) => {
  if (!isOpen) return null;

  const isCurrentEV = isEVProfile(vehicleState.engineProfile);
  const [selectedCategory, setSelectedCategory] = useState<EngineCategory>(
    isCurrentEV ? 'EV' : 'COMBUSTION'
  );

  const characterModes: CharacterMode[] = ['smooth', 'sport', 'aggressive'];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70 backdrop-blur-md transition-opacity">
      <div className="w-full max-w-lg h-full bg-slate-950 border-l border-slate-800 p-6 flex flex-col justify-between overflow-y-auto shadow-2xl">
        {/* Top Header */}
        <div className="flex items-center justify-between border-b border-slate-800/80 pb-4">
          <div>
            <h2 className="text-lg font-black tracking-wider uppercase text-white">
              Vehicle Settings
            </h2>
            <p className="text-xs text-slate-400">Configure sound & powertrain character</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-900 text-slate-400 hover:text-white border border-slate-800 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Settings Body */}
        <div className="flex flex-col gap-6 py-6 flex-1">
          {/* Safety Alert */}
          <div className="p-3.5 rounded-xl bg-amber-950/40 border border-amber-500/40 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200/90 leading-relaxed">
              <strong className="block font-bold text-amber-300 mb-0.5">Safety Notice</strong>
              Set up your sound before driving. Always keep your attention on the road. Do not operate the touchscreen while in motion.
            </div>
          </div>

          {/* Engine / Soundscape Profile Selection */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                Propulsion Sound Profile
              </label>
              <span className="text-[10px] text-cyan-400 font-mono font-bold">
                {isCurrentEV
                  ? 'DIRECT DRIVE (EV)'
                  : ENGINE_PROFILES[vehicleState.engineProfile as CombustionProfileId]?.transmissionProfile?.sourceType || 'FACTORY_OEM'}
              </span>
            </div>

            {/* Category Filter Tabs */}
            <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-800">
              <button
                type="button"
                onClick={() => setSelectedCategory('COMBUSTION')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  selectedCategory === 'COMBUSTION'
                    ? 'bg-red-600 text-white shadow-[0_0_10px_rgba(220,38,38,0.5)]'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Flame className="w-3.5 h-3.5" />
                <span>Combustion ({COMBUSTION_PROFILE_IDS.length})</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCategory('EV')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  selectedCategory === 'EV'
                    ? 'bg-cyan-500 text-slate-950 font-black shadow-[0_0_10px_rgba(6,182,212,0.5)]'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Zap className="w-3.5 h-3.5" />
                <span>EV Soundscapes ({EV_PROFILE_IDS.length})</span>
              </button>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
              {selectedCategory === 'COMBUSTION' ? (
                COMBUSTION_PROFILE_IDS.map(id => {
                  const conf = ENGINE_PROFILES[id];
                  const isSelected = vehicleState.engineProfile === id;
                  const txSource = conf.transmissionProfile?.sourceType;
                  
                  let badgeClass = 'text-emerald-400 bg-emerald-950/60 border-emerald-800/60';
                  let badgeText = 'OEM 8HP';
                  if (txSource === 'SIMULATION_ESTIMATE') {
                    badgeClass = 'text-amber-400 bg-amber-950/60 border-amber-800/60';
                    badgeText = 'SIM ESTIMATE';
                  } else if (txSource === 'ARTIFICIAL_SIMULATION') {
                    badgeClass = 'text-purple-400 bg-purple-950/60 border-purple-800/60';
                    badgeText = 'ARTIFICIAL GEARS';
                  }

                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onSelectEngine(id)}
                      className={`p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer ${
                        isSelected
                          ? 'border-red-500 bg-red-950/40 shadow-[0_0_12px_rgba(239,68,68,0.3)]'
                          : 'border-slate-800 bg-slate-900/60 hover:bg-slate-900'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span className="text-xs font-black text-white line-clamp-1">{conf.name}</span>
                        </div>
                        <span className="text-[11px] text-slate-400 line-clamp-1">{conf.tagline}</span>
                      </div>

                      <div className="flex items-center justify-between gap-1 mt-2.5 pt-2 border-t border-slate-800/60">
                        <span className="text-[10px] font-mono text-cyan-400 font-bold">
                          {conf.redlineRpm} RPM
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold border ${badgeClass}`}>
                          {badgeText}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                EV_PROFILE_IDS.map(id => {
                  const evConf = EV_SOUND_PROFILES[id];
                  const isSelected = vehicleState.engineProfile === id;

                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onSelectEngine(id)}
                      className={`p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-950/50 shadow-[0_0_12px_rgba(6,182,212,0.4)]'
                          : 'border-slate-800 bg-slate-900/60 hover:bg-slate-900'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span className="text-xs font-black text-white line-clamp-1">{evConf.name}</span>
                        </div>
                        <span className="text-[11px] text-slate-400 line-clamp-1">{evConf.tagline}</span>
                      </div>

                      <div className="flex items-center justify-between gap-1 mt-2.5 pt-2 border-t border-slate-800/60">
                        <span className="text-[10px] font-mono text-cyan-400 font-bold">
                          {evConf.baseFrequency} Hz Base
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold border text-cyan-400 bg-cyan-950/60 border-cyan-800/60">
                          DIRECT DRIVE
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Character Mode */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-black uppercase tracking-widest text-slate-400">
              Sound Character
            </label>
            <div className="grid grid-cols-3 gap-2">
              {characterModes.map(mode => {
                const isSelected = vehicleState.characterMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => onSelectCharacter(mode)}
                    className={`py-2.5 px-3 rounded-xl border text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                      isSelected
                        ? 'border-cyan-500 bg-cyan-950/60 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.3)]'
                        : 'border-slate-800 bg-slate-900/60 text-slate-400 hover:text-white'
                    }`}
                  >
                    {mode}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 px-1">
              {vehicleState.characterMode === 'smooth' && 'Subdued acoustic profile, relaxed cruising tone.'}
              {vehicleState.characterMode === 'sport' && 'Responsive harmonics, authoritative acceleration.'}
              {vehicleState.characterMode === 'aggressive' && 'Open exhaust roar, shift pops, sharp redline limiter.'}
            </p>
          </div>

          {/* Powertrain / Transmission */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-black uppercase tracking-widest text-slate-400">
              Transmission
            </label>
            {isCurrentEV ? (
              <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-slate-400 flex items-center justify-between">
                <span className="font-bold text-slate-300">Single-Speed Direct Drive (EV)</span>
                <span className="text-[10px] font-mono text-cyan-400 font-bold px-2 py-0.5 rounded bg-cyan-950/60 border border-cyan-800/60">
                  CONTINUOUS PROPULSION
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onSelectTransmission('automatic')}
                  className={`py-2.5 rounded-xl border text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                    vehicleState.transmissionMode === 'automatic'
                      ? 'border-cyan-500 bg-cyan-950/60 text-cyan-300'
                      : 'border-slate-800 bg-slate-900/60 text-slate-400'
                  }`}
                >
                  Automatic ({vehicleState.totalGears}-Speed)
                </button>
                <button
                  type="button"
                  onClick={() => onSelectTransmission('manual')}
                  className={`py-2.5 rounded-xl border text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                    vehicleState.transmissionMode === 'manual'
                      ? 'border-cyan-500 bg-cyan-950/60 text-cyan-300'
                      : 'border-slate-800 bg-slate-900/60 text-slate-400'
                  }`}
                >
                  Manual ({vehicleState.totalGears}-Speed)
                </button>
              </div>
            )}
          </div>

          {/* Master Volume & Mute */}
          <div className="flex flex-col gap-2 bg-slate-900/50 p-3.5 rounded-xl border border-slate-800">
            <div className="flex justify-between items-center">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-cyan-400" />
                Engine Volume
              </label>
              <button
                type="button"
                onClick={onToggleMute}
                className="text-[11px] font-bold text-slate-400 hover:text-white flex items-center gap-1 cursor-pointer"
              >
                {vehicleState.isMuted ? (
                  <>
                    <VolumeX className="w-3.5 h-3.5 text-rose-500" />
                    <span className="text-rose-500">MUTED</span>
                  </>
                ) : (
                  <span>Mute</span>
                )}
              </button>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.02"
              value={vehicleState.volume}
              onChange={(e) => onSetVolume(parseFloat(e.target.value))}
              className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400 mt-1"
            />
          </div>

          {/* Speed Units */}
          <div className="flex justify-between items-center py-2 border-t border-slate-800/60">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">
              Speedometer Units
            </span>
            <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-800">
              <button
                type="button"
                onClick={() => onSetSpeedUnit('kmh')}
                className={`px-3 py-1 text-xs font-bold font-mono rounded cursor-pointer ${
                  speedUnit === 'kmh' ? 'bg-cyan-500 text-slate-950 font-black' : 'text-slate-400'
                }`}
              >
                KM/H
              </button>
              <button
                type="button"
                onClick={() => onSetSpeedUnit('mph')}
                className={`px-3 py-1 text-xs font-bold font-mono rounded cursor-pointer ${
                  speedUnit === 'mph' ? 'bg-cyan-500 text-slate-950 font-black' : 'text-slate-400'
                }`}
              >
                MPH
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Privacy & Close */}
        <div className="pt-4 border-t border-slate-800/80 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Your location is processed locally to estimate speed and is never stored or transmitted.</span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 active:scale-95 text-slate-950 font-black text-xs uppercase tracking-widest transition-all cursor-pointer"
          >
            Apply & Return to Driving
          </button>
        </div>
      </div>
    </div>
  );
};
